import React, { useState, useRef, useEffect, memo, useCallback, useMemo } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Download, Loader2, AlertCircle, CheckCircle, Mic, MicOff, Volume2, VolumeX, ArrowLeft } from 'lucide-react';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

const SalesCallConversation = memo(({ onBack, conversationData }) => {
  const { updateCreditsFromResponse } = useCredits();
  const [messages, setMessages] = useState(conversationData?.messages || []);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const messagesEndRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [recognitionSupported, setRecognitionSupported] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const recognitionRef = useRef(null);
  const [performanceScore, setPerformanceScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [customerEmotion, setCustomerEmotion] = useState('neutral');
  const [showPerformanceTracker, setShowPerformanceTracker] = useState(false);
  const [conversationProgress, setConversationProgress] = useState(0);
  const [cumulativeScore, setCumulativeScore] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [conversationMomentum, setConversationMomentum] = useState('maintaining');

  const { selectedPersona, customerPersonas } = conversationData || {};

  // Emotion display component
  const getEmotionIcon = useCallback((emotion) => {
    switch (emotion) {
      case 'angry':
        return '😠';
      case 'sad':
        return '😢';
      case 'neutral':
        return '😐';
      case 'happy':
        return '😊';
      case 'very_happy':
        return '😄';
      default:
        return '😐';
    }
  }, []);

  const getEmotionColor = useCallback((emotion) => {
    switch (emotion) {
      case 'angry':
        return 'text-red-500';
      case 'sad':
        return 'text-blue-500';
      case 'neutral':
        return 'text-gray-500';
      case 'happy':
        return 'text-green-500';
      case 'very_happy':
        return 'text-green-600';
      default:
        return 'text-gray-500';
    }
  }, []);

  const getPerformanceColor = useCallback((score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 4) return 'text-orange-600';
    return 'text-red-600';
  }, []);

  const getPerformanceMessage = useCallback((score) => {
    if (score >= 9) return 'Excellent!';
    if (score >= 7) return 'Great job!';
    if (score >= 5) return 'Good effort!';
    if (score >= 3) return 'Keep trying!';
    return 'Needs improvement';
  }, []);

  const getMomentumColor = useCallback((momentum) => {
    switch (momentum) {
      case 'improving':
        return 'text-green-600';
      case 'declining':
        return 'text-red-600';
      case 'maintaining':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  }, []);

  const getMomentumIcon = useCallback((momentum) => {
    switch (momentum) {
      case 'improving':
        return '📈';
      case 'declining':
        return '📉';
      case 'maintaining':
        return '➡️';
      default:
        return '➡️';
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setRecognitionSupported(true);
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.continuous = true; // Keep listening continuously
      recognitionRef.current = recognition;
    }
  }, []);

  const handleMicClick = useCallback(() => {
    if (!recognitionSupported) return;
    const recognition = recognitionRef.current;
    if (!recognition) return;
    if (isListening) {
      recognition.stop();
      setIsListening(false);
      return;
    }
    
    // Set up event handlers
    recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript;
      setCurrentMessage((prev) => prev ? prev + ' ' + transcript : transcript);
    };
    
    recognition.onerror = (event) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Speech recognition error:', event.error);
      }
      if (event.error !== 'no-speech') {
        setError('Voice recognition error: ' + event.error);
      }
      setIsListening(false);
    };
    
    recognition.onend = () => {
      // Only stop listening if user manually clicked stop
      if (isListening) {
        // Restart listening automatically
        try {
          recognition.start();
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Restart failed:', e);
          }
          setIsListening(false);
        }
      }
    };
    
    recognition.start();
    setIsListening(true);
  }, [recognitionSupported, isListening]);

  const speakText = useCallback((text) => {
    if (!audioEnabled) return;
    
    // Stop any currently speaking and wait a moment
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      // Wait a bit for the cancellation to take effect
      setTimeout(() => {
        speakTextInternal(text);
      }, 100);
      return;
    }
    
    speakTextInternal(text);
  }, [audioEnabled]);

  const speakTextInternal = useCallback((text) => {
    // Split long text into smaller chunks to prevent cutting
    const maxChunkLength = 200; // Characters per chunk
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    
    if (sentences.length === 1 && text.length <= maxChunkLength) {
      // Single sentence or short text - speak directly
      speakChunk(text);
    } else {
      // Multiple sentences or long text - speak in chunks
      speakChunksSequentially(sentences);
    }
  }, []);

  const speakChunk = useCallback((chunk) => {
    const utterance = new SpeechSynthesisUtterance(chunk);
    utterance.rate = 0.8; // Even slower for better completion
    utterance.pitch = 1.0;
    utterance.volume = 0.8;
    
    // Get available voices and find the best British female voice
    const voices = window.speechSynthesis.getVoices();
    
    // Specifically target Google UK English Female for highest quality British female voice
    let britishFemaleVoice = null;
    
    // First, try to find Google UK English Female specifically (highest quality)
    britishFemaleVoice = voices.find(voice => voice.name === 'Google UK English Female');
    
    // If not found, try other high-quality British female voices
    if (!britishFemaleVoice) {
      britishFemaleVoice = voices.find(voice => 
        voice.name === 'Google UK English Female' ||
        voice.name === 'Martha' ||
        voice.name === 'Flo (English (United Kingdom))' ||
        voice.name === 'Sandy (English (United Kingdom))' ||
        voice.name === 'Shelley (English (United Kingdom))' ||
        voice.name === 'Victoria' ||
        voice.name === 'Serena' ||
        voice.name === 'Samantha (English (United Kingdom))' ||
        voice.name === 'Alice (English (United Kingdom))'
      );
    }
    
    // If still not found, try any British voice
    if (!britishFemaleVoice) {
      britishFemaleVoice = voices.find(voice => 
        voice.lang.startsWith('en-GB') ||
        voice.name.toLowerCase().includes('british') ||
        voice.name.toLowerCase().includes('uk')
      );
    }
    
    // If still not found, use any available voice
    if (!britishFemaleVoice) {
      britishFemaleVoice = voices.find(voice => voice.default) || voices[0];
    }
    
    if (britishFemaleVoice) {
      utterance.voice = britishFemaleVoice;
      if (process.env.NODE_ENV === 'development') {
        console.log('Using voice:', britishFemaleVoice.name);
      }
    }
    
    utterance.onend = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Speech synthesis completed');
      }
    };
    
    utterance.onerror = (event) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Speech synthesis error:', event.error);
      }
    };
    
    window.speechSynthesis.speak(utterance);
  }, []);

  const speakChunksSequentially = useCallback((chunks) => {
    let currentIndex = 0;
    
    const speakNextChunk = () => {
      if (currentIndex < chunks.length) {
        const chunk = chunks[currentIndex].trim();
        if (chunk) {
          const utterance = new SpeechSynthesisUtterance(chunk);
          utterance.rate = 0.8;
          utterance.pitch = 1.0;
          utterance.volume = 0.8;
          
          // Set the same voice as before
          const voices = window.speechSynthesis.getVoices();
          const britishFemaleVoice = voices.find(voice => voice.name === 'Google UK English Female') ||
                                   voices.find(voice => voice.name === 'Martha') ||
                                   voices.find(voice => voice.name === 'Flo (English (United Kingdom))') ||
                                   voices.find(voice => voice.name === 'Victoria') ||
                                   voices.find(voice => voice.name === 'Serena') ||
                                   voices.find(voice => voice.name === 'Samantha (English (United Kingdom))') ||
                                   voices.find(voice => voice.name === 'Alice (English (United Kingdom))');
          
          if (britishFemaleVoice) {
            utterance.voice = britishFemaleVoice;
            if (process.env.NODE_ENV === 'development') {
              console.log('Using voice:', britishFemaleVoice.name, 'for sequential chunk');
            }
          }
          
          utterance.onend = () => {
            currentIndex++;
            // Small pause between chunks
            setTimeout(() => {
              speakNextChunk();
            }, 200);
          };
          
          utterance.onerror = (event) => {
            if (process.env.NODE_ENV === 'development') {
              console.error('Speech synthesis error:', event.error);
            }
            currentIndex++;
            speakNextChunk();
          };
          
          window.speechSynthesis.speak(utterance);
        } else {
          currentIndex++;
          speakNextChunk();
        }
      }
    };
    
    speakNextChunk();
  }, []);

  const handleSpeakClick = useCallback((text) => {
    speakText(text);
  }, [speakText]);

  const toggleAudio = useCallback(() => {
    setAudioEnabled(!audioEnabled);
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
  }, [audioEnabled]);

  const sendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || isSending) return;
    
    const userMessage = {
      id: messages.length + 1,
      role: 'user',
      content: currentMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsSending(true);
    
    try {
      const response = await axios.post(getApiUrl('/sales_call_message'), {
        message: userMessage,
        persona: selectedPersona,
        conversation_history: messages.map(m => ({
          role: m.role,
          content: m.content
        }))
      }, {
        withCredentials: true,
      });
      
      const customerMessage = {
        id: messages.length + 2,
        role: 'customer',
        content: response.data.customer_response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, customerMessage]);
      
      // Update performance tracking data
      if (response.data.performance_score !== undefined) {
        const newScore = response.data.performance_score;
        const newMessageCount = messageCount + 1;
        
        // Update cumulative score
        const newCumulativeScore = Math.round((cumulativeScore * messageCount + newScore) / newMessageCount);
        
        // Update conversation progress (based on number of exchanges)
        const newProgress = Math.min(100, (newMessageCount / 2) * 20); // Progress based on message exchanges
        
        // Update performance history
        const newPerformanceHistory = [...performanceHistory, {
          message: userMessage.content,
          score: newScore,
          feedback: response.data.feedback,
          suggestion: response.data.suggestion,
          emotion: response.data.customer_emotion,
          timestamp: new Date()
        }];
        
        setPerformanceScore(newScore);
        setCumulativeScore(newCumulativeScore);
        setMessageCount(newMessageCount);
        setConversationProgress(newProgress);
        setPerformanceHistory(newPerformanceHistory);
        setFeedback(response.data.feedback);
        setSuggestion(response.data.suggestion);
        setCustomerEmotion(response.data.customer_emotion);
        setConversationMomentum(response.data.conversation_momentum || 'maintaining');
        
        // Show performance tracker after first message exchange
        if (messages.length === 1) {
          setShowPerformanceTracker(true);
        }
      }
      
      // Auto-speak customer response if audio is enabled
      if (audioEnabled) {
        setTimeout(() => speakText(response.data.customer_response), 800);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  }, [currentMessage, isSending, messages, selectedPersona, audioEnabled, speakText, messageCount, cumulativeScore, performanceHistory]);

  const downloadTranscript = useCallback(async () => {
    if (!messages.length) return;
    
    try {
      const response = await axios.post(getApiUrl('/download_transcript'), {
        messages: messages
      }, {
        withCredentials: true,
      });
      setDownloadUrl(response.data.downloadUrl);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate transcript. Please try again.');
    }
  }, [messages]);

  const handleDownload = useCallback(async () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Back to Setup</span>
              </button>
              <div className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-gray-800">Sales Call Simulator</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Customer:</span>
                <span className="text-sm font-medium text-gray-800">
                  {customerPersonas?.find(p => p.id === selectedPersona)?.name}
                </span>
              </div>
            </div>
            
            {/* Audio Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleAudio}
                className={`p-2 rounded-lg transition-colors ${
                  audioEnabled
                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={audioEnabled ? 'Disable audio' : 'Enable audio'}
              >
                {audioEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Performance Tracker */}
        {showPerformanceTracker && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                {/* Customer Emotion */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Customer Mood:</span>
                  <span className={`text-3xl ${getEmotionColor(customerEmotion)} transition-all duration-300 transform hover:scale-110`}>
                    {getEmotionIcon(customerEmotion)}
                  </span>
                </div>
                
                {/* Conversation Progress */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Conversation Progress:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-blue-600">{conversationProgress}%</span>
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all duration-500"
                        style={{ width: `${conversationProgress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                {/* Cumulative Performance Score */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Overall Score:</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-lg font-bold ${getPerformanceColor(cumulativeScore)}`}>
                      {cumulativeScore}/10
                    </span>
                    <span className="text-sm text-gray-600">
                      {getPerformanceMessage(cumulativeScore)}
                    </span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-500"
                        style={{ width: `${(cumulativeScore / 10) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                {/* Current Message Score */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Last Message:</span>
                  <div className="flex items-center space-x-1">
                    <span className={`text-sm font-bold ${getPerformanceColor(performanceScore)}`}>
                      {performanceScore}/10
                    </span>
                    <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-500"
                        style={{ width: `${(performanceScore / 10) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                {/* Conversation Momentum */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Momentum:</span>
                  <div className="flex items-center space-x-1">
                    <span className={`text-lg ${getMomentumColor(conversationMomentum)}`}>
                      {getMomentumIcon(conversationMomentum)}
                    </span>
                    <span className={`text-sm font-medium ${getMomentumColor(conversationMomentum)}`}>
                      {conversationMomentum}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Toggle Performance Tracker */}
              <button
                onClick={() => setShowPerformanceTracker(!showPerformanceTracker)}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPerformanceTracker ? 'Hide' : 'Show'} Tracker
              </button>
            </div>
            
            {/* Feedback and Suggestions */}
            <div className="mt-3 space-y-2">
              {feedback && (
                <div className="bg-white rounded-lg p-3 border-l-4 border-blue-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Feedback:</span> {feedback}
                  </p>
                </div>
              )}
              {suggestion && (
                <div className="bg-white rounded-lg p-3 border-l-4 border-green-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Suggestion:</span> {suggestion}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        <div className="h-96 overflow-y-auto p-6 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">
                    {message.role === 'user' ? 'You' : 'Customer'}
                  </span>
                  {message.role === 'customer' && (
                    <button
                      onClick={() => handleSpeakClick(message.content)}
                      className="ml-2 p-1 rounded hover:bg-gray-200 transition-colors"
                      title="Speak message"
                    >
                      <Volume2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <p className="text-sm">{message.content}</p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="border-t p-4">
          <form onSubmit={sendMessage} className="flex space-x-2">
            <div className="flex-1 flex items-center space-x-2">
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSending}
              />
              {recognitionSupported && (
                <button
                  type="button"
                  onClick={handleMicClick}
                  className={`p-2 rounded-lg transition-colors ${
                    isListening
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  title={isListening ? 'Stop listening' : 'Start voice input'}
                >
                  {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={!currentMessage.trim() || isSending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isSending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Download Transcript Button */}
      {messages.length > 0 && !downloadUrl && (
        <div className="mt-6">
          <button
            onClick={downloadTranscript}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="h-5 w-5" />
            <span>Generate Transcript</span>
          </button>
        </div>
      )}

      {downloadUrl && (
        <div className="mt-6">
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="h-5 w-5" />
            <span>Download Transcript</span>
          </button>
        </div>
      )}
    </div>
  );
});

SalesCallConversation.displayName = 'SalesCallConversation';

export default SalesCallConversation; 